import { Injectable } from '@angular/core';
import { HelpdeskTaskStatusEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.status.enum';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { HelpDeskSettingsService } from '@zaoblako/web/core/services/helpdesk/help-desk-settings.service';
import { HelpDeskTaskTypeService } from '@zaoblako/web/core/services/helpdesk/help-desk-task-type.service';
import { HelpDeskTaskService } from '@zaoblako/web/core/services/helpdesk/help-desk-task.service';
import { HdPrefixService } from '@zaoblako/web/core/services/prefix/hd-prefix.service';
import { AuthService } from '@zaoblako/web/core/services/security/auth.service';
import { ExternalUserService } from '@zaoblako/web/core/services/security/external-user.service';
import { StoreService } from '@zaoblako/web/core/services/store.service';
import { TitleService } from '@zaoblako/web/core/services/title.service';
import { KeysOfType } from '@zaoblako/web/core/types/keys-of.type';
import { HdTaskState, HelpdeskCardAccessRights } from '@zaoblako/web/modules/help-desk/components/task/card/models/hd-task-state';
import { hdTaskInitialState } from '@zaoblako/web/modules/help-desk/components/task/card/state/hd-task-initial-state';
import { CardEntityPatchBaseline } from '@zaoblako/web/shared/components/card-layout/universal-card-v2/card-entity-patch-baseline.util';
import { mergeHelpdeskTaskAfterPatch, rememberHelpdeskTaskTypeEntityTypeSnapshot } from '@zaoblako/web/shared/components/card-layout/universal-card-v2/merge-helpdesk-task-after-patch.util';
import type { EavEntityTypeFindInterface } from '@zaoblako/shared/modules/eav/dtos/entity-type/eav-entity-type-find.dto';
import type { ClientHelpdeskTaskFormType } from '@zaoblako/web/modules/help-desk/dumbs/helpdesk-accept-task-form/helpdesk-accept-task-form.component';
import type { AnalyticalFeaturesHelpdeskTaskFormType } from '@zaoblako/web/modules/help-desk/dumbs/helpdesk-confirm-task-modal/helpdesk-confirm-task-modal.component';
import type { HelpdeskTaskRequiredFieldsInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-required-fields.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { HelpdeskChangeStatusFormInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-change-status-form.dto';
import type { HelpdeskNotificationSettingsFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-notification-settings.dto';
import type { HelpdeskPartialUpdateFormInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-partial-update-form.dto';
import type { EavEntityFindInterface } from '@zaoblako/shared/modules/eav/dtos/entity/eav-entity-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import type { HelpdeskTaskTypeListQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-list-query.dto';

import { isNil } from 'lodash';
import { BehaviorSubject, finalize, forkJoin, map, Observable, of, switchMap, take, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class HdTaskStoreService extends StoreService<HdTaskState> {
  protected state = new BehaviorSubject(hdTaskInitialState);

  private taskTypeEntityTypeSnapshot: EavEntityTypeFindInterface | null = null;
  private taskTypeEntityTypeSnapshotTaskTypeId: number | null = null;
  private readonly patchBaseline = new CardEntityPatchBaseline<HelpdeskTaskFindInterface>();

  public get getState() {
    return this.state.value;
  }

  public get accessRights(): HelpdeskCardAccessRights {
    return {
      canReadDeal: this.accessService.finance.dealRights.canReadDeal
    };
  }

  public constructor(
    private readonly helpDeskTaskService: HelpDeskTaskService,
    private readonly externalUserService: ExternalUserService,
    private readonly authService: AuthService,
    private readonly helpDeskTaskTypeService: HelpDeskTaskTypeService,
    private readonly titleService: TitleService,
    private readonly hdPrefixService: HdPrefixService,
    private readonly helpDeskSettingsService: HelpDeskSettingsService,
    private readonly accessService: AccessService
  ) {
    super();
  }

  public initState(number: string) {
    this.loadTask(number);
    this.loadEntities();
    this.loadPanelsInfo();
  }

  public loadTask(number: string) {
    this.updateState({ loading: true });

    const taskNumber: number = parseInt(this.hdPrefixService.removePrefix(number), 10);
    const findByIdOrNumber = this.hdPrefixService.hasPrefix(number)
      ? this.helpDeskTaskService.findByNumber<HelpdeskTaskFindInterface>(taskNumber)
      : this.helpDeskTaskService.findById<HelpdeskTaskFindInterface>(taskNumber);

    forkJoin([findByIdOrNumber, this.helpDeskTaskService.getRequiredFields()]).subscribe({
      next: ([task, requiredFields]) => {
        if (!this.hdPrefixService.hasPrefix(number)) {
          window.history.replaceState({}, '', `/helpdesk/task/card/${this.hdPrefixService.getPrefixedNumber(task)}`);
        }
        this.titleService.setTitle(`Заявка ${this.hdPrefixService.getPrefixedNumber(task)}`);
        this.patchBaseline.sync(task);
        this.rememberTaskTypeEntityType(task);
        this.updateState({ task, requiredFields, loading: false });
        this.loadTaskTypes();
      },
      error: () => this.updateState({ loading: false })
    });
  }

  public loadTaskTypes() {
    const queryParams: HelpdeskTaskTypeListQueryInterface = {};
    if (this.getState.task?.client) {
      queryParams.clientIds = [this.getState.task?.client?.id];
    }
    this.helpDeskTaskTypeService.find<HelpdeskTaskTypeFindInterface>(queryParams).subscribe({
      next: (taskTypes) => {
        const task: HelpdeskTaskFindInterface = { ...this.getState.task };
        task.taskType = taskTypes.find((type) => type?.id === task.taskType?.id);

        this.updateState({
          task,
          taskTypes
        });
      }
    });
  }

  public loadEntities() {
    forkJoin([
      this.helpDeskSettingsService.getNotificationSettings(),
      this.helpDeskTaskService.getStatuses()
    ]).subscribe(([helpdeskNotificationSettings, taskStatuses]) => {
      this.updateState({
        helpdeskNotificationSettings,
        taskStatuses: taskStatuses ?? []
      });
    });
  }

  public syncPatchBaselineFromTask = (): void => {
    this.patchBaseline.sync(this.getState.task ?? null);
  };

  public getPatchFieldBaseline = (key: string): unknown => this.patchBaseline.fieldValue(key);

  public isPatchFieldUnchanged = (key: string, value: unknown): boolean =>
    this.patchBaseline.isFieldUnchanged(key, value);

  public commitPatchField = (key: string, value: unknown): void => {
    this.patchBaseline.commitField(key, value);
  };

  public updateFieldTask(key: keyof HelpdeskPartialUpdateFormInterface, value: any) {
    this.patchFieldTask$(key, value).subscribe();
  }

  public patchEavEntity$ = (eavEntity: EavEntityFindInterface): Observable<HelpdeskTaskFindInterface> =>
    this.patchFieldTask$('eavEntity', eavEntity || null);

  public patchFieldTask$ = (
    key: keyof HelpdeskPartialUpdateFormInterface,
    value: unknown
  ): Observable<HelpdeskTaskFindInterface> => {
    const mutationKey = String(key);
    if (this.patchBaseline.isFieldUnchanged(mutationKey, value)) {
      return of(this.getState.task);
    }
    const task: HelpdeskTaskFindInterface = { ...this.getState.task, [key]: value || null };
    this.updateState({ task });
    return this.helpDeskTaskService.patch({ id: this.getState.task?.id, [key]: value || null }).pipe(
      tap(() => this.commitPatchField(mutationKey, value)),
      map(() => this.getState.task)
    );
  };

  public changeClient(client: ClientFindInterface) {
    this.updateState({ showActionButtons: false });
    const task: HelpdeskTaskFindInterface = this.getState.task;

    this.helpDeskTaskService
      .patch(<HelpdeskPartialUpdateFormInterface & { id?: number }>{
        id: this.getState.task?.id,
        client: client,
        direction: null,
        externalUserAuditors: null
      })
      .pipe(
        switchMap(() =>
          this.getState.task.externalUser
            ? this.externalUserService.patch({
                id: this.getState.task.externalUser?.id,
                client
              })
            : of([])
        )
      )
      .subscribe({
        next: () => {
          this.updateState({
            task: {
              ...task,
              client: client,
              taskType: null,
              externalUserAuditors: []
            },
            showActionButtons: true
          });
          this.loadTaskTypes();
        },
        error: () => {
          this.updateState({ showActionButtons: true });
        }
      });
  }

  public loadFiles() {
    this.updateState({
      filesInfo: {
        files: this.getState.filesInfo.files,
        loading: true
      }
    });

    this.helpDeskTaskService.getFiles(this.getState.task?.id).subscribe({
      next: (files) => {
        this.updateState({
          filesInfo: {
            files: files.items,
            loading: false
          }
        });
      }
    });
  }

  public updatePanelsInfo(key: KeysOfType<typeof this.getState.panelsInfo, any>, value: any) {
    const info = this.getState.panelsInfo;
    info[key] = value;
    this.updateState({ panelsInfo: info });
    localStorage.setItem('hdTask.panelsInfo', JSON.stringify(info));
  }

  public loadPanelsInfo() {
    if (localStorage.getItem('hdTask.panelsInfo')) {
      const panelsInfo = JSON.parse(localStorage.getItem('hdTask.panelsInfo'));
      Object.keys(this.getState.panelsInfo).forEach((key) => {
        panelsInfo[key] = panelsInfo.hasOwnProperty(key) ? panelsInfo[key] : this.getState.panelsInfo[key];
      });
      this.updateState({ panelsInfo });
    }
  }

  public changeStatus(data: {
    status: HelpdeskChangeStatusFormInterface;
    clientInfo?: ClientHelpdeskTaskFormType;
    confirmInfo?: AnalyticalFeaturesHelpdeskTaskFormType;
  }): void {
    this.updateState({ showActionButtons: false, loadingChangeStatus: true });

    const task: HelpdeskTaskFindInterface = { ...this.getState.task };
    let patchBody: HelpdeskPartialUpdateFormInterface & { id: number; } | null = null;

    if (data.clientInfo) {
      task.client = data.clientInfo.client;
      task.externalUser = data.clientInfo.externalUser;
      task.taskType = data.clientInfo.taskType;
      task.unit = data.clientInfo.unit;

      if (!task.responsible) {
        task.responsible = this.authService.currentUser;
      }
      patchBody = {
        id: this.getState.task.id,
        client: data.clientInfo.client,
        externalUser: data.clientInfo.externalUser,
        taskType: data.clientInfo.taskType,
        unit: data.clientInfo.unit
      };
    } else if (data.confirmInfo) {
      task.taskType = data.confirmInfo.taskType;
      task.unit = data.confirmInfo.unit;
      task.deal = data.confirmInfo.deal;
      task.client = data.confirmInfo.client;
      patchBody = {
        id: this.getState.task.id,
        taskType: data.confirmInfo.taskType,
        unit: data.confirmInfo.unit,
        deal: data.confirmInfo.deal,
        client: data.confirmInfo.client
      };
    }

    const request$ = patchBody
      ? this.patchExternalUserIfNeeded(task).pipe(switchMap(() => this.helpDeskTaskService.patch(patchBody)))
      : of(null);

    request$
      .pipe(
        switchMap(() => this.helpDeskTaskService.changeStatus(this.getState.task?.id, data.status)),
        take(1),
        finalize(() => this.updateState({ loadingChangeStatus: false, showActionButtons: true }))
      )
      .subscribe({
        next: (newStatus) => {
          task.status = newStatus;
          if (data.status.status === HelpdeskTaskStatusEnum.IN_PROCESS) {
            task.responsible = this.authService.currentUser;
          }
          this.updateState({ task });
          this.loadTaskTypes();
        }
      });
  }

  private patchExternalUserIfNeeded(task: HelpdeskTaskFindInterface): Observable<any> {
    const needsMigration =
      task.externalUser &&
      task.client?.externalUsers?.length &&
      !task.client.externalUsers.find((eu) => eu.id === task.externalUser?.id);

    if (needsMigration) {
      return this.externalUserService.patch({
        id: task.externalUser?.id,
        client: task.client
      });
    }

    return of(null);
  }

  public delegate(selectedUser: UserFindInterface) {
    this.updateState({ showActionButtons: false });

    this.helpDeskTaskService.patch({ id: this.getState.task?.id, responsible: selectedUser }).subscribe({
      next: () => {
        const task: HelpdeskTaskFindInterface = { ...this.getState.task };
        task.responsible = selectedUser;
        this.updateState({ task, showActionButtons: true });
      },
      error: () => {
        this.updateState({ showActionButtons: true });
      }
    });
  }

  public toggleEdited(editedField: typeof this.getState.editedField) {
    if (editedField === this.getState.editedField) {
      this.updateState({ editedField: null });
    } else {
      this.updateState({ editedField });
    }
  }

  public mergePatchedHelpdeskTask = (
    patched: HelpdeskTaskFindInterface,
    selectedTaskType?: HelpdeskTaskTypeFindInterface | null
  ): HelpdeskTaskFindInterface =>
    mergeHelpdeskTaskAfterPatch(patched, {
      previousTask: this.getState.task,
      taskTypes: this.getState.taskTypes,
      selectedTaskType,
      taskTypeEntityTypeSnapshot: this.taskTypeEntityTypeSnapshot,
      taskTypeEntityTypeSnapshotTaskTypeId: this.taskTypeEntityTypeSnapshotTaskTypeId
    });

  public changeTask(task: HelpdeskTaskFindInterface) {
    const merged = this.mergePatchedHelpdeskTask(task);
    this.rememberTaskTypeEntityType(merged);
    this.updateState({ task: { ...merged } });
  }

  private rememberTaskTypeEntityType = (task: HelpdeskTaskFindInterface): void => {
    const snapshot = rememberHelpdeskTaskTypeEntityTypeSnapshot(task);
    if (isNil(snapshot)) {
      return;
    }
    this.taskTypeEntityTypeSnapshotTaskTypeId = snapshot.taskTypeId;
    this.taskTypeEntityTypeSnapshot = snapshot.entityType;
  };

  public changeCountComment(countComments: number) {
    this.updateState({ countComments });
  }

  public getFactTime() {
    this.helpDeskTaskService.getFactTime(this.getState.task?.id).subscribe({
      next: (data) => {
        const task: HelpdeskTaskFindInterface = { ...this.getState.task };
        task.factMin = data?.factMin;
        this.updateState({ task });
      }
    });
  }

  public updateRequiredFields(requiredFields: HelpdeskTaskRequiredFieldsInterface) {
    this.updateState({
      requiredFields: { ...requiredFields }
    });
  }

  public updateDefaultClient(client: ClientFindInterface) {
    this.updateState({
      defaultClient: { ...client }
    });
  }

  public updateNotificationSettings(helpdeskNotificationSettings: HelpdeskNotificationSettingsFindInterface) {
    this.updateState({
      helpdeskNotificationSettings
    });
  }

  public updateFilesCount(count: number) {
    this.updateState((state) => ({ task: { ...state.task, filesCount: state.task.filesCount + count } }));
  }

  public setLoading(loading: boolean) {
    this.updateState({ loading });
  }
}
